import { Link } from 'react-router';

import {
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import RepairItemStatusBadge from '#/modules/crmMgmt/aftersales/repairItem/components/RepairItemStatusBadge';
import { getRepairItemDocumentLinks } from '#/modules/crmMgmt/aftersales/repairItem/helper/repairItemLinks';
import {
  readRepairRequestDateRangeFilter,
  resolveRepairRequestDateFilterChipDisplay,
} from '#/modules/crmMgmt/aftersales/repairItem/helper/repairRequestDateListFilter';
import { REPAIR_ITEM_STATUS_FILTER_OPTIONS } from '#/modules/crmMgmt/aftersales/repairItem/helper/repairItemStatus';
import { formatDateOrDash } from '#/utils/date';

const DATE_FILTER_PLACEHOLDER = '西元年/月/日';

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

function stopRowNavigation(event) {
  event.stopPropagation();
}

function renderDocumentLink(to, label) {
  return (
    <Link
      to={to}
      className="text-primary-600 underline"
      onClick={stopRowNavigation}
      onDoubleClick={stopRowNavigation}
    >
      {label}
    </Link>
  );
}

export function buildRepairItemColumns({
  filters = {},
  onFilterChange,
  onRepairRequestDateRangeChange,
} = {}) {
  return [
    {
      id: 'repairRequestNo',
      header: '報修單號',
      accessorKey: 'repairRequestNo',
      size: 140,
      enableSorting: true,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'repairRequestNo',
        label: '報修單號',
        filterValue: filters.repairRequestNo,
        onChange: (value) => onFilterChange?.('repairRequestNo', value),
        placeholder: '搜尋報修單號',
      }),
      cell: ({ row }) => {
        const links = getRepairItemDocumentLinks(row.original);
        const label = displayDash(row.original.repairRequestNo);
        if (!links.repairRequestTo) {
          return label;
        }
        return renderDocumentLink(links.repairRequestTo, label);
      },
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 110,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => (
        row.original.status
          ? <RepairItemStatusBadge status={row.original.status} size="sm" />
          : '—'
      ),
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: REPAIR_ITEM_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange?.('status', value),
        }),
      },
    },
    {
      id: 'repairRequestDate',
      header: '報修日期',
      accessorFn: (row) => formatDateOrDash(row.repairRequestDate),
      size: 120,
      minSize: 120,
      enableSorting: true,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'repairRequestDateFrom',
        label: '報修日期',
        mode: 'range',
        filterValue: readRepairRequestDateRangeFilter(filters),
        onChange: onRepairRequestDateRangeChange,
        placeholder: DATE_FILTER_PLACEHOLDER,
        resolveFilterDisplay: resolveRepairRequestDateFilterChipDisplay,
      }),
    },
    {
      id: 'serialNo',
      header: '序號',
      accessorFn: (row) => displayDash(row.serialNo),
      size: 150,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'serialNo',
          label: '序號',
          filterValue: filters.serialNo,
          onChange: (value) => onFilterChange?.('serialNo', value),
          placeholder: '搜尋序號',
        }),
      },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'productCode',
      header: '產品代號',
      accessorFn: (row) => displayDash(row.productCode),
      size: 140,
      enableSorting: true,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'productCode',
        label: '產品代號',
        filterValue: filters.productCode,
        onChange: (value) => onFilterChange?.('productCode', value),
        placeholder: '搜尋產品代號',
      }),
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'itemName',
      header: '品名',
      accessorFn: (row) => displayDash(row.itemName),
      size: 180,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'itemName',
          label: '品名',
          filterValue: filters.itemName,
          onChange: (value) => onFilterChange?.('itemName', value),
          placeholder: '搜尋品名',
        }),
      },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'problemDescription',
      header: '問題',
      accessorFn: (row) => displayDash(row.problemDescription),
      size: 220,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'problemDescription',
          label: '問題',
          filterValue: filters.problemDescription,
          onChange: (value) => onFilterChange?.('problemDescription', value),
          placeholder: '搜尋問題描述',
        }),
      },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'accessories',
      header: '配件',
      accessorFn: (row) => displayDash(row.accessories),
      size: 160,
      enableSorting: false,
      enableColumnFilter: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'remark',
      header: '備註',
      accessorFn: (row) => displayDash(row.remark),
      size: 180,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange?.('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'repairReportId',
      header: '維修報告',
      size: 110,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const links = getRepairItemDocumentLinks(row.original);
        if (!links.repairReportTo) {
          return links.repairReportEmptyLabel;
        }
        return renderDocumentLink(links.repairReportTo, '維修報告');
      },
    },
    {
      id: 'signReceiptId',
      header: '簽收單',
      size: 100,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const links = getRepairItemDocumentLinks(row.original);
        if (!links.signReceiptTo) {
          return null;
        }
        return renderDocumentLink(links.signReceiptTo, '簽收單');
      },
    },
  ];
}
